import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Grid,
  Stack,
} from '@mui/material';
import axiosInstance from '../../api/axiosConfig';

const PublicResult = () => {
  // mode: result | verify
  const [mode, setMode] = useState('result');

  // ===== RESULT STATES =====
  const [registrationId, setRegistrationId] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);

  // ===== VERIFY REGISTRATION STATES =====
  const [verifyInput, setVerifyInput] = useState('');
  const [verifyData, setVerifyData] = useState(null);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyError, setVerifyError] = useState(null);
  const [utrNumber, setUtrNumber] = useState('');

  // ===== TRACK APPLICATION STATES =====

const [applicationNumber, setApplicationNumber] = useState('');

const [applicationData, setApplicationData] = useState(null);

const [applicationLoading, setApplicationLoading] = useState(false);

const [applicationError, setApplicationError] = useState(null);

  // ===== RESULT SEARCH =====
  const handleSearch = async (e) => {
    e.preventDefault();

    if (!registrationId.trim()) {
      setError('Please enter a Registration ID');
      return;
    }

    setLoading(true);
    setError(null);
    setSearched(true);
    setResult(null);

    try {
      const res = await axiosInstance.get(`/public/result/${registrationId}`);
      setResult(res.data.data || res.data);
    } catch {
      setError('Result not found. Please check your Registration ID.');
    } finally {
      setLoading(false);
    }
  };

  // ===== VERIFY REGISTRATION =====
  const handleVerifyRegistration = async (e) => {
    e.preventDefault();

    if (!verifyInput.trim()) {
      setVerifyError('Please enter Registration Number or Mobile Number');
      return;
    }

    setVerifyLoading(true);
    setVerifyError(null);
    setVerifyData(null);

    try {
      const isMobile = /^\d{10}$/.test(verifyInput);

      const url = isMobile
        ? `/public/verify-student?mobile=${verifyInput}`
        : `/public/verify-student?registrationId=${verifyInput}`;

      const res = await axiosInstance.get(url);

      if (!res.data?.isValid) {
        setVerifyError(res.data?.message || 'Student not found');
      } else {
        setVerifyData(res.data.student);
      }
    } catch {
      setVerifyError('Unable to verify student');
    } finally {
      setVerifyLoading(false);
    }
  };

  // ===== TRACK APPLICATION =====

const handleTrackApplication = async (e) => {
  if (e) {
    e.preventDefault();
  }

  if (!applicationNumber.trim()) {
    setApplicationError(
      'Please enter Application Number'
    );
    return;
  }

  try {
    setApplicationLoading(true);
    setApplicationError(null);
    setApplicationData(null);

    const res = await axiosInstance.get(
      `/public/application-status/${applicationNumber}`
    );

    setApplicationData(res.data);
  }
  catch {
    setApplicationError(
      'Application not found'
    );
  }
  finally {
    setApplicationLoading(false);
  }
};

const handleSubmitPayment = async () => {
  if (!utrNumber.trim()) {
  alert('Please enter UTR Number');
  return;
}
  try {

    await axiosInstance.post(
      `/admission/${applicationData.applicationId}/submit-payment`,
      {
        paymentReference: utrNumber
      }
    );

    alert(
      'Payment details submitted successfully'
    );

    handleTrackApplication();

  } catch {

    alert(
      'Failed to submit payment details'
    );

  }
};

const handleDownloadReceipt = async () => {
  try {

    const response = await axiosInstance.get(
      `/public/application-receipt/${applicationNumber}`,
      {
        responseType: 'blob'
      }
    );

    const url = window.URL.createObjectURL(
      new Blob([response.data])
    );

    const link = document.createElement('a');

    link.href = url;

    link.download =
      `AdmissionReceipt_${applicationNumber}.pdf`;

    document.body.appendChild(link);

    link.click();

    link.remove();

  } catch {

    alert(
      'Unable to download receipt'
    );

  }
};
  // ===== DOWNLOAD MARKSHEET =====
  const handleDownloadMarksheet = async () => {
    if (!result?.resultId) return;

    try {
      const res = await axiosInstance.get(
        `/public/result/${registrationId}/marksheet`,
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Marksheet_${registrationId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      alert('Unable to download marksheet');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>

      {/* HEADER */}
      <Box textAlign="center" mb={5}>
        <Typography variant="h4" fontWeight={700}>
          Institute Verification Portal
        </Typography>
        <Typography color="text.secondary">
          Verify Results & Student Registration
        </Typography>
      </Box>

      {/* MODE SWITCH */}
      <Card sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}>
        <CardContent>
          <Stack direction="row" spacing={2}>

  <Button
    fullWidth
    variant={mode === 'result' ? 'contained' : 'outlined'}
    onClick={() => {
      setMode('result');
    }}
  >
    Verify Result
  </Button>

  <Button
    fullWidth
    variant={mode === 'verify' ? 'contained' : 'outlined'}
    onClick={() => {
      setMode('verify');
    }}
  >
    Verify Registration
  </Button>

  <Button
    fullWidth
    variant={mode === 'application' ? 'contained' : 'outlined'}
    onClick={() => {
      setMode('application');
    }}
  >
    Track Admission
  </Button>

</Stack>
        </CardContent>
      </Card>

      {/* ================= VERIFY RESULT ================= */}
      {mode === 'result' && (
        <>
          <Card sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}>
            <CardContent>
              <form onSubmit={handleSearch}>
                <Stack spacing={2}>
                  <TextField
                    label="Registration Number"
                    placeholder="2024001"
                    fullWidth
                    value={registrationId}
                    onChange={(e) => setRegistrationId(e.target.value.toUpperCase())}
                  />
                  <Button type="submit" variant="contained" disabled={loading}>
                    {loading ? <CircularProgress size={24} /> : 'Search Result'}
                  </Button>
                </Stack>
              </form>
            </CardContent>
          </Card>

          {error && searched && <Alert severity="error">{error}</Alert>}

          {result && (
            <>
              {/* STUDENT DETAILS */}
              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={700} mb={3}>
                    Student Details
                  </Typography>
                  <Grid container spacing={2}>
                    {[
                      ['Name', result.studentName],
                      ['Registration Number', result.registrationNumber],
                      ['Course', result.courseName],
                      ['Exam', result.examName],
                      ['Percentage', `${result.percentage}%`],
                      ['Grade', result.grade],
                      ['Status', result.resultStatus],
                    ].map(([label, value], i) => (
                      <Grid item xs={12} sm={6} key={i}>
                        <Typography variant="caption">{label}</Typography>
                        <Typography fontWeight={600}>{value}</Typography>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>

              {/* SUBJECT MARKS */}
              {result.subjects?.length > 0 && (
                <Card sx={{ mb: 4 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={700} mb={2}>
                      Subject Wise Marks
                    </Typography>
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Subject</TableCell>
                            <TableCell align="right">Max</TableCell>
                            <TableCell align="right">Obtained</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {result.subjects.map((s, i) => (
                            <TableRow key={i}>
                              <TableCell>{s.subjectName}</TableCell>
                              <TableCell align="right">{s.maxMarks}</TableCell>
                              <TableCell align="right">
                                <strong>{s.marksObtained}</strong>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              )}

              {/* DOWNLOAD */}
              {/* <Card sx={{ bgcolor: '#e8f5e9' }}>
                <CardContent>
                  <Button
                    fullWidth
                    variant="contained"
                    color="success"
                    onClick={handleDownloadMarksheet}
                  >
                    Download Marksheet (PDF)
                  </Button>
                </CardContent>
              </Card> */}
            </>
          )}
        </>
      )}

      {/* ================= VERIFY REGISTRATION ================= */}
      {mode === 'verify' && (
        <>
          <Card sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}>
            <CardContent>
              <form onSubmit={handleVerifyRegistration}>
                <Stack spacing={2}>
                  <TextField
                    label="Registration Number or Mobile Number"
                    placeholder="12345 or 9876543210"
                    fullWidth
                    value={verifyInput}
                    onChange={(e) => setVerifyInput(e.target.value)}
                  />
                  <Button type="submit" variant="contained" disabled={verifyLoading}>
                    {verifyLoading ? <CircularProgress size={24} /> : 'Verify Registration'}
                  </Button>
                </Stack>
              </form>
            </CardContent>
          </Card>

          {verifyError && <Alert severity="error">{verifyError}</Alert>}

          {verifyData && (
            <Card sx={{ maxWidth: 800, mx: 'auto' }}>
              <CardContent>
                <Typography variant="h6" fontWeight={700} mb={3}>
                  Student Registration Details
                </Typography>
                <Grid container spacing={2}>
                  {[
                    ['Name', verifyData.fullName],
                    ['Registration Number', verifyData.registrationNumber],
                    ['Mobile', verifyData.mobile],
                    ['Course', verifyData.courseName],
                    ['Batch Year', verifyData.batchYear],
                    ['Admission Date', verifyData.admissionDate],
                    ['Status', verifyData.status],
                  ].map(([label, value], i) => (
                    <Grid item xs={12} sm={6} key={i}>
                      <Typography variant="caption">{label}</Typography>
                      <Typography fontWeight={600}>{value}</Typography>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* ================= TRACK ADMISSION ================= */}

{mode === 'application' && (
  <>
    <Card
      sx={{
        maxWidth: 600,
        mx: 'auto',
        mb: 4
      }}
    >
      <CardContent>

        <form onSubmit={handleTrackApplication}>
          <Stack spacing={2}>

            <TextField
              label="Application Number"
              placeholder="2026000001"
              fullWidth
              value={applicationNumber}
              onChange={(e) =>
                setApplicationNumber(
                  e.target.value
                )
              }
            />

            <Button
              type="submit"
              variant="contained"
              disabled={applicationLoading}
            >
              {applicationLoading
                ? <CircularProgress size={24} />
                : 'Track Admission'}
            </Button>

          </Stack>
        </form>

      </CardContent>
    </Card>

    {applicationError && (
      <Alert severity="error">
        {applicationError}
      </Alert>
    )}

    {applicationData && (
      <Card
        sx={{
          maxWidth: 900,
          mx: 'auto'
        }}
      >
        <CardContent>

          <Typography
            variant="h6"
            fontWeight={700}
            mb={3}
          >
            Admission Status
          </Typography>

          <Grid container spacing={3}>

            <Grid item xs={12} sm={6}>
              <Typography variant="caption">
                Application Number
              </Typography>

              <Typography fontWeight={600}>
                {applicationData.applicationNumber}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="caption">
                Student Name
              </Typography>

              <Typography fontWeight={600}>
                {applicationData.fullName}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="caption">
                Course
              </Typography>

              <Typography fontWeight={600}>
                {applicationData.courseName}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="caption">
                Branch
              </Typography>

              <Typography fontWeight={600}>
                {applicationData.branchName}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="caption">
                Application Status
              </Typography>

              <Typography
                fontWeight={700}
                color={
                  applicationData.applicationStatus ===
                  'Approved'
                    ? 'success.main'
                    : applicationData.applicationStatus ===
                      'Rejected'
                    ? 'error.main'
                    : 'warning.main'
                }
              >
                {applicationData.applicationStatus}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="caption">
                Payment Status
              </Typography>

              <Typography
                fontWeight={700}
                color={
                  applicationData.paymentStatus ===
                  'Paid'
                    ? 'success.main'
                    : 'warning.main'
                }
              >
                {applicationData.paymentStatus}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
  <Typography variant="caption">
    UTR Number
  </Typography>

  <Typography fontWeight={600}>
    {applicationData.paymentReference || '-'}
  </Typography>
</Grid>
<Grid item xs={12} sm={6}>
  <Typography variant="caption">
    Admission Fee
  </Typography>

  <Typography fontWeight={600}>
    ₹ {applicationData.admissionFee || 0}
  </Typography>
</Grid>
<Grid item xs={12} sm={6}>
  <Typography variant="caption">
    Applied On
  </Typography>

  <Typography fontWeight={600}>
    {applicationData.appliedDate
      ? new Date(
          applicationData.appliedDate
        ).toLocaleDateString()
      : '-'}
  </Typography>
</Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="caption">
                Registration Number
              </Typography>

              <Typography fontWeight={600}>
                {applicationData.registrationNumber || '-'}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="caption">
                Registration ID
              </Typography>

              <Typography fontWeight={600}>
                {applicationData.registrationId || '-'}
              </Typography>
            </Grid>

          </Grid>
<Box mt={4}>
  <Alert
    severity={
      applicationData.applicationStatus === 'Approved'
        ? 'success'
        : applicationData.applicationStatus === 'Rejected'
        ? 'error'
        : 'info'
    }
  >
    {applicationData.applicationStatus === "Approved"
      ? "Congratulations! Your admission has been approved."
      : applicationData.applicationStatus === "Rejected"
      ? "Your admission application has been rejected."
      : applicationData.paymentStatus === "Submitted"
      ? "Payment details submitted successfully. Waiting for institute approval."
      : "Your application is currently under review."}
  </Alert>
</Box>
{applicationData.paymentStatus === 'Pending' && (

  <Box mt={4}>

    <Typography
      variant="h6"
      fontWeight={700}
      gutterBottom
    >
      Complete Payment
    </Typography>

    <Typography mb={2}>
      Admission Fee :
      ₹ {applicationData.admissionFee}
    </Typography>

    <Box
      component="img"
      src="/paymentqr.jpeg"
      alt="Payment QR"
      sx={{
    width: 250,
    height: 250
  }}
    />

    <TextField
      fullWidth
      label="UTR / Transaction Number"
      value={utrNumber}
      onChange={(e) =>
        setUtrNumber(
          e.target.value
        )
      }
      sx={{ mb: 2 }}
    />

    <Button
      fullWidth
      variant="contained"
      onClick={handleSubmitPayment}
    >
      Submit Payment
    </Button>

  </Box>

)}
<Box mt={4}>

  {(applicationData.paymentStatus === 'Submitted' ||
    applicationData.applicationStatus === 'Approved') && (

    <Button
      fullWidth
      variant="contained"
      color="success"
      onClick={handleDownloadReceipt}
    >
      Download Admission Receipt PDF
    </Button>

  )}

</Box>
        </CardContent>
      </Card>
    )}
  </>
)}
    </Container>
  );
};

export default PublicResult;
